/* PDF Styling Editor — user-editable controls for the Quotation Letter PDF.
 * Wires up multi-logo upload, colour pickers, font selectors, size sliders,
 * section visibility toggles and an editable letter-copy block. Includes a
 * "Preview PDF" button that streams a sample quotation in a new tab using
 * the currently-saved styling.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, Trash2, Image as ImageIcon, Eye, Save, RefreshCw, Star } from 'lucide-react';
import LetterheadLayoutEditor from './LetterheadLayoutEditor';

const SECTION_LABELS = {
  letterhead_background:   'Full-page letterhead background (page 1)',
  mini_header_pages_2plus: 'Mini-header on page 2 onwards',
  page_numbers:            'Page numbers',
  roof_details_table:      'Roof Details summary table',
  discounts_block:         'Discounts applied block',
  footer:                  'Footer (contact strip)',
  secondary_logo:          'Roof Tiling Remedies mascot (bottom-right of every page)',
};

const COLOUR_LABELS = {
  heading: 'Heading & Title',
  accent:  'Accent (rules / underlines)',
  body:    'Body text',
  price:   'Price / Totals',
  footer:  'Footer text',
};

const SIZE_RANGES = {
  title:   { min: 14, max: 40, label: 'Title (QUOTE)' },
  heading: { min: 9,  max: 24, label: 'Section heading' },
  body:    { min: 7,  max: 16, label: 'Body text' },
  price:   { min: 9,  max: 24, label: 'Price block' },
  small:   { min: 6,  max: 14, label: 'Footer / small text' },
};

const LETTER_COPY_FIELDS = [
  { key: 'intro',         label: 'Intro paragraph (shown under the address block)',     rows: 3 },
  { key: 'payment_terms', label: 'Payment Terms paragraph',                              rows: 2 },
  { key: 'commencement',  label: 'Commencement paragraph',                               rows: 2 },
  { key: 'validity',      label: 'Validity paragraph',                                   rows: 2 },
  { key: 'general',       label: 'General Conditions paragraph',                         rows: 2 },
];

// Cover Letter paragraphs — sent alongside the Quotation PDF. Each variant
// (no_discount / with_discount) is edited independently. Defaults are kept
// server-side; leaving a field blank falls back to the legacy Access copy.
const COVER_LETTER_FIELDS = [
  { key: 'intro',         label: 'Intro (after RE: address line)',                       rows: 3 },
  { key: 'para_about',    label: 'About RTR paragraph',                                  rows: 4 },
  { key: 'para_values',   label: 'Values / Quality paragraph',                           rows: 4 },
  { key: 'para_close',    label: 'Close paragraph (quality + fitness for use)',          rows: 4 },
  { key: 'para_offer',    label: 'Offer paragraph (5% restoration discount + house wash)', rows: 4 },
  { key: 'signoff_name',  label: 'Sign-off name',                                        rows: 1 },
  { key: 'signoff_title', label: 'Sign-off title',                                       rows: 1 },
];

// Appraisal Authorisation Letter — pre-inspection sign-off.
const AUTH_LETTER_FIELDS = [
  { key: 'intro',             label: 'Intro paragraph (above details block)',            rows: 3 },
  { key: 'verify_prompt',     label: 'Verify-your-details prompt',                        rows: 2 },
  { key: 'instruction',       label: 'Instruction paragraph (about inspection scope)',    rows: 3 },
  { key: 'quality_statement', label: 'Quality statement (below signature)',               rows: 3 },
  { key: 'products_prompt',   label: '"Are you interested in?" prompt',                   rows: 1 },
  { key: 'legal_clause',      label: 'Legal clause (credit enquiries + pre-existing)',    rows: 3 },
];

export default function PDFStylingEditor() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [styling, setStyling] = useState(null);
  const [fonts, setFonts] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/report-settings/pdf-styling');
      setStyling(data.styling);
      setFonts(data.fonts || []);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put('/report-settings/pdf-styling', patch);
      setStyling(data.styling);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', file.name);
    const { data } = await api.post('/report-settings/pdf-styling/logos', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setStyling(s => ({ ...s, logos: data.logos, primary_logo_index: data.primary_logo_index }));
  };

  const deleteLogo = async (index) => {
    if (!window.confirm('Delete this logo?')) return;
    const { data } = await api.delete(`/report-settings/pdf-styling/logos/${index}`);
    setStyling(s => ({ ...s, logos: data.logos, primary_logo_index: data.primary_logo_index }));
  };

  const openPreview = () => {
    const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    // Use a token-bearing fetch + Blob URL so the preview works without
    // exposing the JWT in the URL.
    api.get('/reports/quotation-preview', { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        window.open(url, '_blank');
      })
      .catch(() => {
        window.open(`${API_BASE}/api/reports/quotation-preview`, '_blank');
      });
  };

  if (loading || !styling) return <div className="text-center py-12 text-slate-400">Loading PDF styling…</div>;

  return (
    <div data-testid="pdf-styling-editor" className="space-y-6">
      {/* Sticky header with Save + Preview */}
      <div className="flex items-center justify-between gap-3 sticky top-0 bg-white z-10 pb-3 border-b border-slate-100">
        <div>
          <h3 className="text-lg font-bold text-slate-800">PDF Styling</h3>
          <p className="text-xs text-slate-500 mt-0.5">Logos, colours, fonts, sizes, section visibility and letter copy for the Quotation PDF.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="pdf-styling-preview"
            onClick={openPreview}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border border-slate-200 text-slate-700 hover:border-[#D35400] hover:text-[#D35400] transition-colors"
          >
            <Eye size={14} /> Preview PDF
          </button>
          <button
            type="button"
            data-testid="pdf-styling-reload"
            onClick={load}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border border-slate-200 text-slate-700 hover:border-slate-400 disabled:opacity-40"
            title="Reload from server"
          >
            <RefreshCw size={14} />
          </button>
          {savedFlash && <span className="text-xs text-emerald-600 font-semibold">Saved</span>}
        </div>
      </div>

      {/* Letterhead variant selector — picks the artwork drawn as a
          full-page background on page 1 of every customer PDF. Restores
          the Access-classic 2025 design or switches to the minimalist
          2026 refresh without a code change. */}
      <LetterheadVariantPicker api={api} />

      {/* Programmatic letterhead layout editor — only usable when the
          active variant is `programmatic_v1`; otherwise renders a hint
          + one-click switcher. */}
      <LetterheadLayoutEditor api={api} />

      {/* Logos */}
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-slate-800">Logos ({styling.logos?.length || 0}/8)</h4>
          <input
            ref={fileRef}
            type="file"
            data-testid="pdf-styling-logo-input"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={e => { uploadLogo(e.target.files[0]); e.target.value = ''; }}
          />
          <button
            type="button"
            data-testid="pdf-styling-logo-upload"
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-[#D35400] text-white hover:bg-[#A04000]"
          >
            <Upload size={12} /> Upload Logo
          </button>
        </div>
        {(!styling.logos || styling.logos.length === 0) ? (
          <p className="text-xs italic text-slate-400">No logos uploaded yet. Upload a PNG / JPG / SVG to use on your quotations.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {styling.logos.map((l, idx) => {
              const isPrimary = idx === (styling.primary_logo_index || 0);
              return (
                <div key={idx} data-testid={`pdf-styling-logo-card-${idx}`} className={`relative border rounded-md overflow-hidden ${isPrimary ? 'border-[#D35400] ring-2 ring-[#D35400]/30' : 'border-slate-200'}`}>
                  <div className="h-24 bg-slate-50 flex items-center justify-center p-2">
                    <img src={l.data_url} alt={l.label} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="px-2 py-1.5 bg-white flex items-center justify-between gap-1">
                    <span className="text-[10px] text-slate-600 truncate flex-1" title={l.label}>{l.label}</span>
                    <button
                      type="button"
                      data-testid={`pdf-styling-logo-primary-${idx}`}
                      onClick={() => save({ primary_logo_index: idx })}
                      title={isPrimary ? 'Primary logo' : 'Set as primary'}
                      className={`p-1 rounded ${isPrimary ? 'text-[#D35400]' : 'text-slate-300 hover:text-[#D35400]'}`}
                    >
                      <Star size={12} fill={isPrimary ? '#D35400' : 'none'} />
                    </button>
                    <button
                      type="button"
                      data-testid={`pdf-styling-logo-delete-${idx}`}
                      onClick={() => deleteLogo(idx)}
                      className="p-1 rounded text-slate-300 hover:text-rose-600"
                      title="Delete logo"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-3 grid grid-cols-3 gap-3">
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">Logo position</span>
            <select
              data-testid="pdf-styling-logo-position"
              value={styling.logo_position}
              onChange={e => save({ logo_position: e.target.value })}
              className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs"
            >
              <option value="top-left">Top left</option>
              <option value="top-centre">Top centre</option>
              <option value="top-right">Top right</option>
            </select>
          </label>
          <label className="text-xs col-span-2">
            <span className="block font-semibold text-slate-600 mb-1">Logo scale ({Math.round((styling.logo_scale || 1) * 100)}%)</span>
            <input
              data-testid="pdf-styling-logo-scale"
              type="range" min="0.5" max="2" step="0.05"
              value={styling.logo_scale || 1}
              onChange={e => setStyling(s => ({ ...s, logo_scale: Number(e.target.value) }))}
              onMouseUp={() => save({ logo_scale: styling.logo_scale })}
              onTouchEnd={() => save({ logo_scale: styling.logo_scale })}
              className="w-full accent-[#D35400]"
            />
          </label>
        </div>
      </section>

      {/* Colours */}
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-bold text-slate-800 mb-3">Colours</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(COLOUR_LABELS).map(([key, label]) => (
            <label key={key} className="text-xs">
              <span className="block font-semibold text-slate-600 mb-1">{label}</span>
              <div className="flex items-center gap-1.5">
                <input
                  data-testid={`pdf-styling-colour-${key}`}
                  type="color"
                  value={styling.colours?.[key] || '#000000'}
                  onChange={e => setStyling(s => ({ ...s, colours: { ...s.colours, [key]: e.target.value } }))}
                  onBlur={() => save({ colours: styling.colours })}
                  className="w-10 h-8 rounded border border-slate-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={styling.colours?.[key] || ''}
                  onChange={e => setStyling(s => ({ ...s, colours: { ...s.colours, [key]: e.target.value } }))}
                  onBlur={() => save({ colours: styling.colours })}
                  className="flex-1 px-2 py-1 border border-slate-200 rounded font-mono text-[11px]"
                />
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Fonts */}
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-bold text-slate-800 mb-3">Fonts</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.keys(styling.fonts || {}).map(role => (
            <label key={role} className="text-xs">
              <span className="block font-semibold text-slate-600 mb-1 capitalize">{role} font</span>
              <select
                data-testid={`pdf-styling-font-${role}`}
                value={styling.fonts[role]}
                onChange={e => save({ fonts: { ...styling.fonts, [role]: e.target.value } })}
                className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs"
              >
                {fonts.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </label>
          ))}
        </div>
      </section>

      {/* Sizes */}
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-bold text-slate-800 mb-3">Font Sizes</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {Object.entries(SIZE_RANGES).map(([key, range]) => (
            <label key={key} className="text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-slate-600">{range.label}</span>
                <span className="font-mono text-slate-500">{styling.sizes?.[key] ?? '—'} pt</span>
              </div>
              <input
                data-testid={`pdf-styling-size-${key}`}
                type="range" min={range.min} max={range.max} step="1"
                value={styling.sizes?.[key] ?? range.min}
                onChange={e => setStyling(s => ({ ...s, sizes: { ...s.sizes, [key]: Number(e.target.value) } }))}
                onMouseUp={() => save({ sizes: styling.sizes })}
                onTouchEnd={() => save({ sizes: styling.sizes })}
                className="w-full accent-[#D35400]"
              />
            </label>
          ))}
        </div>
      </section>

      {/* Sections */}
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-bold text-slate-800 mb-3">Section Visibility</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(SECTION_LABELS).map(([key, label]) => {
            const checked = styling.sections?.[key] !== false;
            return (
              <label key={key} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  data-testid={`pdf-styling-section-${key}`}
                  checked={checked}
                  onChange={e => save({ sections: { ...styling.sections, [key]: e.target.checked } })}
                  className="accent-[#D35400]"
                />
                <span className="text-xs text-slate-700">{label}</span>
              </label>
            );
          })}
        </div>
      </section>

      {/* Letter Copy — Quotation Letter */}
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-bold text-slate-800 mb-3">Quotation Letter Copy</h4>
        <div className="space-y-3">
          {LETTER_COPY_FIELDS.map(f => (
            <label key={f.key} className="block text-xs">
              <span className="block font-semibold text-slate-600 mb-1">{f.label}</span>
              <textarea
                data-testid={`pdf-styling-copy-${f.key}`}
                rows={f.rows}
                value={styling.letter_copy?.[f.key] || ''}
                onChange={e => setStyling(s => ({ ...s, letter_copy: { ...s.letter_copy, [f.key]: e.target.value } }))}
                onBlur={() => save({ letter_copy: styling.letter_copy })}
                className="w-full px-3 py-2 border border-slate-200 rounded text-xs leading-relaxed"
              />
            </label>
          ))}
        </div>
      </section>

      {/* Cover Letter (sent with quotations) */}
      <CoverLetterEditor
        styling={styling}
        setStyling={setStyling}
        save={save}
      />

      {/* Appraisal Authorisation Letter */}
      <AuthLetterEditor
        styling={styling}
        setStyling={setStyling}
        save={save}
      />

      <div className="flex justify-end pt-2">
        <button
          type="button"
          data-testid="pdf-styling-save-all"
          onClick={() => save({
            colours: styling.colours,
            fonts: styling.fonts,
            sizes: styling.sizes,
            sections: styling.sections,
            letter_copy: styling.letter_copy,
            logo_scale: styling.logo_scale,
            logo_position: styling.logo_position,
          })}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-[#D35400] text-white hover:bg-[#A04000] disabled:opacity-40"
        >
          <Save size={14} /> Save all changes
        </button>
      </div>
    </div>
  );
}


// ---- COVER LETTER editor sub-section ---------------------------------------
function CoverLetterEditor({ styling, setStyling, save }) {
  const [variant, setVariant] = useState('no_discount');
  const [open, setOpen] = useState(true);
  const copy = styling.letter_copy?.cover_letter?.[variant] || {};

  const setField = (k, v) => {
    setStyling(s => ({
      ...s,
      letter_copy: {
        ...s.letter_copy,
        cover_letter: {
          ...(s.letter_copy?.cover_letter || {}),
          [variant]: { ...((s.letter_copy?.cover_letter || {})[variant] || {}), [k]: v },
        },
      },
    }));
  };
  const flush = () => save({ letter_copy: styling.letter_copy });
  const resetVariant = async () => {
    if (!window.confirm(`Reset the "${variant === 'no_discount' ? 'No-discount' : 'With-discount'}" cover-letter copy to factory defaults? This clears any custom text for this variant.`)) return;
    const next = { ...(styling.letter_copy || {}) };
    if (next.cover_letter) {
      delete next.cover_letter[variant];
      if (Object.keys(next.cover_letter).length === 0) delete next.cover_letter;
    }
    setStyling(s => ({ ...s, letter_copy: next }));
    await save({ letter_copy: next });
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4" data-testid="pdf-styling-cover-letter">
      <header className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-bold text-slate-800">Cover Letter Copy</h4>
          <span className="text-[10px] text-slate-400">Sent alongside the Quotation PDF</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="cover-letter-variant"
            value={variant}
            onChange={e => setVariant(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1"
          >
            <option value="no_discount">No-discount variant</option>
            <option value="with_discount">With-discount variant</option>
          </select>
          <button
            type="button"
            data-testid="cover-letter-reset"
            onClick={resetVariant}
            className="text-xs text-slate-500 hover:text-red-500 px-2 py-1 border border-slate-200 rounded"
            title="Clear custom text — fall back to factory defaults"
          >
            <RefreshCw size={11} className="inline mr-1" />Reset
          </button>
          <button
            type="button"
            data-testid="cover-letter-collapse"
            onClick={() => setOpen(v => !v)}
            className="text-xs text-slate-400 hover:text-slate-700 px-2 py-1"
          >{open ? 'Hide' : 'Show'}</button>
        </div>
      </header>
      {open && (
        <div className="space-y-3">
          <p className="text-[11px] text-slate-500 -mt-1">
            Leave any field blank to use the factory text. The <span className="font-semibold">No-discount</span> variant
            is currently used by the Cover Letter PDF endpoint; the <span className="font-semibold">With-discount</span> variant
            is reserved for the upcoming restoration-discount letter.
          </p>
          {COVER_LETTER_FIELDS.map(f => (
            <label key={f.key} className="block text-xs">
              <span className="block font-semibold text-slate-600 mb-1">{f.label}</span>
              {f.rows === 1 ? (
                <input
                  type="text"
                  data-testid={`cover-letter-${variant}-${f.key}`}
                  value={copy[f.key] || ''}
                  onChange={e => setField(f.key, e.target.value)}
                  onBlur={flush}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-xs"
                />
              ) : (
                <textarea
                  data-testid={`cover-letter-${variant}-${f.key}`}
                  rows={f.rows}
                  value={copy[f.key] || ''}
                  onChange={e => setField(f.key, e.target.value)}
                  onBlur={flush}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-xs leading-relaxed"
                />
              )}
            </label>
          ))}
        </div>
      )}
    </section>
  );
}


// ---- AUTHORISATION LETTER editor sub-section -------------------------------
function AuthLetterEditor({ styling, setStyling, save }) {
  const [open, setOpen] = useState(true);
  const copy = styling.letter_copy?.appraisal_authorisation || {};
  // Treat product_options as a newline-separated textarea on the UI side.
  const productsText = Array.isArray(copy.product_options)
    ? copy.product_options.join('\n')
    : (copy.product_options || '');

  const setField = (k, v) => {
    setStyling(s => ({
      ...s,
      letter_copy: {
        ...s.letter_copy,
        appraisal_authorisation: { ...(s.letter_copy?.appraisal_authorisation || {}), [k]: v },
      },
    }));
  };
  const flush = () => {
    // Coerce product_options back to an array on blur.
    const arr = (productsText || '').split('\n').map(s => s.trim()).filter(Boolean);
    const cleaned = { ...(styling.letter_copy?.appraisal_authorisation || {}) };
    if (arr.length) cleaned.product_options = arr;
    save({ letter_copy: { ...styling.letter_copy, appraisal_authorisation: cleaned } });
  };
  const resetAll = async () => {
    if (!window.confirm('Reset the Appraisal Authorisation Letter copy to factory defaults? Any custom wording will be cleared.')) return;
    const next = { ...(styling.letter_copy || {}) };
    delete next.appraisal_authorisation;
    setStyling(s => ({ ...s, letter_copy: next }));
    await save({ letter_copy: next });
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4" data-testid="pdf-styling-auth-letter">
      <header className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-bold text-slate-800">Appraisal Authorisation Letter Copy</h4>
          <span className="text-[10px] text-slate-400">Pre-inspection sign-off letter</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="auth-letter-reset"
            onClick={resetAll}
            className="text-xs text-slate-500 hover:text-red-500 px-2 py-1 border border-slate-200 rounded"
            title="Clear custom text — fall back to factory defaults"
          >
            <RefreshCw size={11} className="inline mr-1" />Reset
          </button>
          <button
            type="button"
            data-testid="auth-letter-collapse"
            onClick={() => setOpen(v => !v)}
            className="text-xs text-slate-400 hover:text-slate-700 px-2 py-1"
          >{open ? 'Hide' : 'Show'}</button>
        </div>
      </header>
      {open && (
        <div className="space-y-3">
          <p className="text-[11px] text-slate-500 -mt-1">
            Leave any field blank to use the factory text. Product options are entered one per line and shown as
            checkboxes on the printed letter.
          </p>
          {AUTH_LETTER_FIELDS.map(f => (
            <label key={f.key} className="block text-xs">
              <span className="block font-semibold text-slate-600 mb-1">{f.label}</span>
              {f.rows === 1 ? (
                <input
                  type="text"
                  data-testid={`auth-letter-${f.key}`}
                  value={copy[f.key] || ''}
                  onChange={e => setField(f.key, e.target.value)}
                  onBlur={flush}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-xs"
                />
              ) : (
                <textarea
                  data-testid={`auth-letter-${f.key}`}
                  rows={f.rows}
                  value={copy[f.key] || ''}
                  onChange={e => setField(f.key, e.target.value)}
                  onBlur={flush}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-xs leading-relaxed"
                />
              )}
            </label>
          ))}
          <label className="block text-xs">
            <span className="block font-semibold text-slate-600 mb-1">Product options (one per line)</span>
            <textarea
              data-testid="auth-letter-product_options"
              rows={5}
              value={productsText}
              onChange={e => setField('product_options', e.target.value.split('\n').map(s => s).filter(s => true))}
              onBlur={flush}
              className="w-full px-3 py-2 border border-slate-200 rounded text-xs leading-relaxed"
              placeholder={'Roof Cleaning\nMoss and Mould Treatment\nRoof Restoration\nAnnual Routine Maintenance\nRe Roof'}
            />
          </label>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// LetterheadVariantPicker — dropdown to switch between the shipped letterhead
// artworks. Stored under `report_settings.letterhead_variant` so a change
// takes effect on the next PDF render (no restart needed).
// ---------------------------------------------------------------------------
function LetterheadVariantPicker({ api }) {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);
  const [variants, setVariants] = React.useState([]);
  const [active, setActive] = React.useState('');
  const [defaultKey, setDefaultKey] = React.useState('');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/report-settings/letterhead-variants');
      setVariants(data.variants || []);
      setActive(data.active || data.default || '');
      setDefaultKey(data.default || '');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load letterhead variants');
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => { load(); }, [load]);

  const changeVariant = async (key) => {
    setSaving(true);
    try {
      await api.put('/report-settings', { letterhead_variant: key });
      setActive(key);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      alert('Failed to save letterhead variant: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="letterhead-variant-picker"
      className="rounded-md border border-slate-200 bg-white p-4"
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <h4 className="text-sm font-bold text-slate-800">Letterhead Artwork</h4>
          <p className="text-[11px] text-slate-500 mt-0.5 max-w-2xl leading-relaxed">
            Choose which letterhead is drawn as the page-1 background on every
            customer PDF (Quotations, Instant Roof Report, Cover Letters,
            Terms of Sale, etc.). Content margins auto-adjust to each artwork&apos;s
            safe area — you don&apos;t need to tweak margins yourself.
          </p>
        </div>
        {savedFlash && <span className="text-xs text-emerald-600 font-semibold whitespace-nowrap">✓ Saved</span>}
      </div>
      {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
      {loading ? (
        <div className="text-xs text-slate-400">Loading variants…</div>
      ) : (
        <div className="space-y-2">
          {variants.map(v => {
            const isActive = active === v.key;
            const isDefault = defaultKey === v.key;
            return (
              <label
                key={v.key}
                data-testid={`letterhead-variant-${v.key}`}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  isActive
                    ? 'border-[#316536] bg-emerald-50/60'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                } ${saving ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <input
                  type="radio"
                  name="letterhead-variant"
                  value={v.key}
                  checked={isActive}
                  onChange={() => changeVariant(v.key)}
                  disabled={saving || !v.asset_exists}
                  data-testid={`letterhead-variant-radio-${v.key}`}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{v.label}</span>
                    {isActive && (
                      <span className="text-[10px] px-1.5 py-0 rounded bg-[#316536] text-white font-mono uppercase tracking-wide">
                        active
                      </span>
                    )}
                    {isDefault && !isActive && (
                      <span className="text-[10px] px-1.5 py-0 rounded bg-slate-100 text-slate-500 font-mono uppercase tracking-wide">
                        default
                      </span>
                    )}
                    {!v.asset_exists && (
                      <span className="text-[10px] px-1.5 py-0 rounded bg-red-50 text-red-700 font-mono uppercase tracking-wide">
                        missing asset
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 leading-relaxed mt-1">{v.description}</p>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </section>
  );
}

